import React, { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import {
  backend,
  Dates,
  eventBackend,
  GroupListData,
  statsBackend,
} from "../../services";
import { AddUserGroupModalProps } from "../../types";
import { SelectInput, TextInput } from "../components";
import { defaultCurrencyId } from "../../constants";

interface FormDataGroup {
  group_id: number;
  dates: Dates;
  value: number;
  currency_id: number;
}

const GroupAddSalePlanModal: React.FC<AddUserGroupModalProps> = ({
  group_id,
  onSave,
  closeModal,
}) => {
  const [pending, setPending] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null); // Ref для модального окна
  const [formData, setFormData] = useState<FormDataGroup>({
    group_id: Number(group_id),
    dates: {
      month: new Date().getMonth() + 1, // Текущий месяц (1-12)
      year: new Date().getFullYear(), // Текущий год
    },
    value: 0,
    currency_id: defaultCurrencyId, // Пример: ID валюты по умолчанию
  });

  useEffect(() => {}, []);

  // Обработчик изменения месяца
  const handleMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedMonth = Number(event.target.value);
    setFormData((prev) => ({
      ...prev,
      dates: {
        ...prev.dates,
        month: selectedMonth,
      },
    }));
  };

  // Обработчик изменения года
  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedYear = Number(event.target.value);
    setFormData((prev) => ({
      ...prev,
      dates: {
        ...prev.dates,
        year: selectedYear,
      },
    }));
  };

  // Обработчик изменения суммы
  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    setFormData((prev) => ({
      ...prev,
      value: value,
    }));
  };

  // Обработчик изменения валюты
  const handleCurrencyChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const currencyId = Number(event.target.value);
    setFormData((prev) => ({
      ...prev,
      currency_id: currencyId,
    }));
  };

  // Обработчик клика за пределами модального окна
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        closeModal(); // Закрываем модальное окно
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [closeModal]);

  const saveAction = () => {
    if (pending) return;
    if (!group_id) return;
    if (formData.value <= 0) {
      toast("Введите корректный план продаж");
      return;
    }
    setPending(true);
    statsBackend
      .setupGroupSalePlan({
        group_id,
        month: formData.dates.month,
        year: formData.dates.year,
        currency_id: formData.currency_id,
        value: formData.value,
      })
      .then(() => {
        onSave();
        toast("Данные сохранены");
        closeModal();
      })
      .catch(() => {
        toast("Оштбка сохранения данных");
      })
      .finally(() => {
        setPending(false);
      });
  };

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const years = Array.from({ length: 5 }, (_, i) => currentYear + i);

  return (
    <div className="editModal" ref={modalRef}>
      <div className="modalHeading">
        <h3>Установить план продаж группы</h3>
      </div>
      <div className="columnCenter creationForm">
        <SelectInput
          label="Месяц"
          value={formData.dates.year}
          options={months.map((month) => ({
            value: month,
            label: new Date(2023, month - 1).toLocaleString("default", {
              month: "long",
            }),
            disabled:
              formData.dates.year === currentYear && month < currentMonth,
          }))}
          onChange={handleMonthChange}
        />

        <SelectInput
          label="Год"
          value={formData.dates.year}
          options={years.map((year) => ({
            value: year,
            label: String(year),
          }))}
          onChange={handleYearChange}
        />

        {/* Поле: Валюта */}
        <SelectInput
          label="Валюта"
          value={formData.currency_id}
          options={[
            {
              value: 2,
              label: "USD",
            },
            {
              value: 1,
              label: "RUB",
            },
          ]}
          onChange={handleCurrencyChange}
        />

        {/* Поле: Сумма */}
        <TextInput
          label="Сумма"
          type="number"
          value={String(formData.value)}
          onChange={handleValueChange}
        />
      </div>
      <div className="flex between btnRow">
        <button className="filterButton" type="button" onClick={saveAction}>
          Сохранить
        </button>
        <button className="cancelButton" type="button" onClick={closeModal}>
          Отменить
        </button>
      </div>
    </div>
  );
};

export default GroupAddSalePlanModal;
